import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
} from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    padding: 20,
  },
  billTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  toBlock: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  toLabel: {
    fontSize: 10,
    color: '#374151',
    marginRight: 10,
    paddingTop: 1,
  },
  companyText: {
    fontSize: 10,
    color: '#374151',
    marginBottom: 2,
  },
  supplierText: {
    fontSize: 10,
    color: '#374151',
    marginBottom: 2,
  },
  meta: {
    textAlign: 'right',
    color: '#374151',
  },
  metaBlock: {
    display: 'flex',
    paddingLeft: 70,
  },
  metaLabel: {
    fontWeight: 'bold',
    fontSize: 10,
    color: '#374151',
  },
  metaValue: {
    fontSize: 10,
    color: '#374151',
    marginBottom: 3,
  },
  table: {
    marginBottom: 24,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    paddingVertical: 5,
    paddingHorizontal: 4,
    fontSize: 8,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f9fafb',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  colDesc: { width: '35%', paddingRight: 8 },
  colQty: { width: '12%', textAlign: 'right', paddingRight: 12 },
  colRate: { width: '18%', textAlign: 'right', paddingRight: 12 },
  colGst: { width: '15%', textAlign: 'right', paddingRight: 10, paddingLeft: 12 },
  colAmount: { width: '20%', textAlign: 'right', paddingLeft: 10 },
  headerText: {
    fontWeight: 'bold',
    color: '#374151',
  },
  totalsWrap: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  totalsBox: {
    width: 180,
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  totalsDivider: {
    borderTopWidth: 2,
    borderTopColor: '#1f2937',
    paddingTop: 8,
    marginTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontWeight: 'bold',
  },
  dueDateRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 10,
  },
  footer: {
    marginTop: 30,
    textAlign: 'center',
    fontSize: 11,
    color: '#000000',
    fontFamily: 'Helvetica-Bold',
  },
});

function getTaxRatePercent(lineItem, taxes = []) {
  if (!lineItem?.tax_rate_id) return null;
  const tax = taxes.find((t) => t.tax_id === lineItem.tax_rate_id);
  if (tax) return parseFloat(tax.tax_rate);
  if (lineItem.tax_rate_id === 'GST') return 10;
  return null;
}

function formatGstRate(ratePercent) {
  if (ratePercent == null) return '—';
  return `${Number(ratePercent)}%`;
}

function getTotalGstLabel(formData, lineItems = [], taxes = []) {
  if (formData?.tax_inclusion === 'no_tax') return null;
  const firstWithTax = lineItems.find((li) => li.tax_rate_id);
  const rate = firstWithTax != null ? getTaxRatePercent(firstWithTax, taxes) : null;
  if (rate != null) return `TOTAL ${rate}%`;
  return 'TOTAL TAX';
}

export default function BillPdfDocument({ draftBill }) {
  if (!draftBill) return null;

  const { formData, lineItems = [], subtotal, taxTotal, grandTotal, taxes = [], company } = draftBill;
  const showGst = formData?.tax_inclusion !== 'no_tax';
  const totalGstLabel = showGst ? getTotalGstLabel(formData, lineItems, taxes) : null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}-${month}-${d.getFullYear()}`;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerRow}>
          {/* Left: To + Company info */}
          <View style={styles.toBlock}>
            <Text style={styles.toLabel}>To:</Text>
            <View>
              <Text style={styles.companyText}>{company?.companyname || '—'}</Text>
              {company?.street && <Text style={styles.companyText}>{company.street}</Text>}
              {(company?.city || company?.state || company?.postcode) && (
                <Text style={styles.companyText}>
                  {[company.city, company.state, company.postcode].filter(Boolean).join(' ')}
                </Text>
              )}
              {company?.country && <Text style={styles.companyText}>{company.country}</Text>}
            </View>
          </View>

          {/* Center: Invoice Date */}
          <View style={{ alignItems: 'center' }}>
            <Text style={[styles.metaLabel, { fontFamily: 'Helvetica-Bold' }]}>Invoice Date</Text>
            <Text style={styles.metaValue}>{formatDate(formData.bill_date)}</Text>
          </View>

          {/* Right: Supplier info */}
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={styles.supplierText}>{formData.supplier_name || '—'}</Text>
          </View>
        </View>

        {/* Line Items Table */}
        <View style={styles.table}>
          <View fixed style={styles.tableHeader}>
            <Text style={[styles.colDesc, styles.headerText]}>Description</Text>
            <Text style={[styles.colQty, styles.headerText]}>Quantity</Text>
            <Text style={[styles.colRate, styles.headerText]}>Unit Price</Text>
            {showGst && <Text style={[styles.colGst, styles.headerText]}>GST</Text>}
            <Text style={[styles.colAmount, styles.headerText]}>Amount AUD</Text>
          </View>
          {(lineItems || []).map((li, idx) => {
            const ratePercent = getTaxRatePercent(li, taxes);
            const gstDisplay = showGst ? formatGstRate(ratePercent) : null;

            return (
              <View key={li.id || idx} style={styles.tableRow}>
                <Text style={styles.colDesc}>{li.item_description || ''}</Text>
                <Text style={styles.colQty}>{li.quantity ?? ''}</Text>
                <Text style={styles.colRate}>{Number(li.unit_price || 0).toFixed(2)}</Text>
                {showGst && <Text style={styles.colGst}>{gstDisplay}</Text>}
                <Text style={styles.colAmount}>{Number(li.total_price || 0).toFixed(2)}</Text>
              </View>
            );
          })}
        </View>

        {/* Totals */}
        <View style={styles.totalsWrap} wrap={false}>
          <View style={styles.totalsBox}>
            <View style={styles.totalsRow}>
              <Text>Subtotal</Text>
              <Text>{Number(subtotal || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
            </View>
            {totalGstLabel != null && (
              <View style={styles.totalsRow}>
                <Text>{totalGstLabel}</Text>
                <Text>{Number(taxTotal || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
              </View>
            )}
            <View style={styles.totalsDivider}>
              <Text style={{ fontWeight: 'bold', fontSize: 12 }}>TOTAL AUD</Text>
              <Text style={{ fontWeight: 'bold', fontSize: 12 }}>{Number(grandTotal || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
            </View>
          </View>
        </View>

        {formData.due_date && (
          <View style={styles.totalsWrap}>
            <View style={styles.totalsRow}>
              <View style={{ width: 180, alignItems: 'left' }}>
                <Text style={{ fontSize: 10, color: '#374151' }}>DUE DATE: {formatDate(formData.due_date)}</Text>
              </View>
            </View>
          </View>
        )}

        {/* Footer */}
        <Text style={styles.footer}>This is not a tax invoice</Text>

        {/* Page number */}
        <Text
          style={{
            position: 'absolute',
            bottom: 12,
            left: 15,
            fontSize: 8,
            color: '#6b7280',
          }}
          fixed
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
        />
        <Text
          style={{
            position: 'absolute',
            bottom: 12,
            left: 0,
            right: 0,
            textAlign: 'center',
            fontSize: 8,
            color: '#9ca3af',
          }}
          fixed
        >
          Powered by runbusiness.io
        </Text>
      </Page>
    </Document>
  );
}
